import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpHelper } from '@umss/shared/utils';
import {
  InvoiceGroupBackfillParams,
  InvoiceGroupBackfillResponse,
  JobDetailDto,
  StripeCustomerAddressBackfillResult,
  StripeExternalAccountsReconcileResponse,
  StripeTaxInfoBackfillResponse
} from '../dtos/pay-cycle-tools';

/**
 * Client for the Stripe data backfill endpoints (Business Admin "Backfills" page).
 * Jobs are SuperAdmin-only and run asynchronously via Hangfire; the reconcile and
 * tax-info backfills return a `jobId` that is polled with {@link getJob}.
 */
@Injectable({ providedIn: 'root' })
export class BackfillService {
  private readonly http = inject(HttpClient);

  // POST /api/Admin/stripe/customer-addresses/backfill
  backfillStripeCustomerAddresses(): Observable<StripeCustomerAddressBackfillResult> {
    return this.http.post<StripeCustomerAddressBackfillResult>(
      '/api/Admin/stripe/customer-addresses/backfill',
      null
    );
  }

  // POST /api/admin/stripe/contractors/tax-info/backfill
  runTaxInfoBackfill(): Observable<StripeTaxInfoBackfillResponse> {
    return this.http.post<StripeTaxInfoBackfillResponse>(
      '/api/admin/stripe/contractors/tax-info/backfill',
      null
    );
  }

  // POST /api/admin/stripe/external-accounts/reconcile
  reconcileStripeExternalAccounts(): Observable<StripeExternalAccountsReconcileResponse> {
    return this.http.post<StripeExternalAccountsReconcileResponse>(
      '/api/admin/stripe/external-accounts/reconcile',
      null
    );
  }

  // POST /api/Admin/invoice-groups/backfill
  // Synchronous: returns the full report (no jobId, no polling).
  backfillInvoiceGroups(
    params: InvoiceGroupBackfillParams
  ): Observable<InvoiceGroupBackfillResponse> {
    return this.http.post<InvoiceGroupBackfillResponse>(
      '/api/Admin/invoice-groups/backfill',
      null,
      { params: HttpHelper.toHttpParams(params) }
    );
  }

  // GET /api/jobs/{jobId}
  getJob(jobId: string): Observable<JobDetailDto> {
    return this.http.get<JobDetailDto>(`/api/jobs/${jobId}`);
  }
}
